// ACORD Library — read-only access to the vetted forms library in FIGVault.
//
// Data source: Agency/ACORD_Library/acord_manifest_final.json (1,144 records)
// plus acord_gaps.json (editions EZLynx lists but does not actually serve).
// Fetched once and held in state; all filtering happens client-side.
//
// This module performs no writes of any kind.

const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const LIBRARY_NAME = 'FIGVault';
const FOLDER = 'Agency/ACORD_Library';
const GRAPH = 'https://graph.microsoft.com/v1.0';

let cachedSiteId = null;
let cachedDriveId = null;

async function resolveSiteId(token) {
  if (cachedSiteId) return cachedSiteId;
  const res = await fetch(`${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Could not resolve SharePoint site (${res.status})`);
  cachedSiteId = (await res.json()).id;
  return cachedSiteId;
}

async function resolveDriveId(token) {
  if (cachedDriveId) return cachedDriveId;
  const siteId = await resolveSiteId(token);
  const res = await fetch(`${GRAPH}/sites/${siteId}/drives`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Could not list document libraries (${res.status})`);
  const drives = (await res.json()).value || [];
  const target = LIBRARY_NAME.toLowerCase();
  const drive = drives.find(d => (d.name || '').toLowerCase() === target);
  if (!drive) {
    const names = drives.map(d => `"${d.name}"`).join(', ');
    throw new Error(`Library "${LIBRARY_NAME}" not found. Available: ${names}`);
  }
  cachedDriveId = drive.id;
  return cachedDriveId;
}

async function fetchJsonFile(token, name) {
  const driveId = await resolveDriveId(token);
  const path = encodeURI(`${FOLDER}/${name}`);
  const res = await fetch(`${GRAPH}/drives/${driveId}/root:/${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Could not read ${name} (${res.status})`);
  const meta = await res.json();
  const url = meta['@microsoft.graph.downloadUrl'];
  if (!url) throw new Error(`No download URL for ${name}`);
  const body = await fetch(url); // pre-authenticated URL: no Authorization header
  if (!body.ok) throw new Error(`Could not download ${name} (${body.status})`);
  return body.json();
}

/** Load the manifest and the gaps list. One call, at mount. */
export async function loadAcordLibrary(token) {
  const [records, gaps] = await Promise.all([
    fetchJsonFile(token, 'acord_manifest_final.json'),
    fetchJsonFile(token, 'acord_gaps.json').catch(() => []), // gaps are optional
  ]);
  return { records, gaps };
}

/**
 * Resolve a single PDF's web URL, on demand only — never pre-checked in bulk.
 * Returns null when the file is not in SharePoint yet (OneDrive still uploading).
 */
export async function resolveFileUrl(token, vaultPath) {
  const driveId = await resolveDriveId(token);
  const path = encodeURI(`${FOLDER}/${vaultPath}`);
  const res = await fetch(`${GRAPH}/drives/${driveId}/root:/${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null; // not synced yet
  if (!res.ok) throw new Error(`Could not open file (${res.status})`);
  const meta = await res.json();
  return meta.webUrl || meta['@microsoft.graph.downloadUrl'] || null;
}

/**
 * Download a PDF's bytes via the drive item's /content endpoint.
 *
 * Deliberately not the webUrl: that renders in Chrome's built-in viewer, which
 * handles AcroForm fields inconsistently, and every form here is fillable.
 * Returns null when the file is not in SharePoint yet.
 */
export async function downloadFileBlob(token, vaultPath) {
  const driveId = await resolveDriveId(token);
  const path = encodeURI(`${FOLDER}/${vaultPath}`);
  const res = await fetch(`${GRAPH}/drives/${driveId}/root:/${path}:/content`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null; // not synced yet
  if (!res.ok) throw new Error(`Could not download file (${res.status})`);
  return res.blob();
}
